import { Command as CommandPrimitive } from 'cmdk';
import * as React from 'react';
import { type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from '../../core/utils/classnames.tsx';
import { withIcon } from '../../core/components/Icon';
import { faCheck, faChevronDown, faEllipsis, faUser } from '@fortawesome/pro-regular-svg-icons';

export type Option = Record<'value' | 'label', string> & Record<string, string>;

type SupportUserSelectProps = {
    options: Option[];
    value?: string;
    onValueChange?: (value: string) => void;
    onSelect?: (event: any, option: Option) => void;
    isLoading?: boolean;
    disabled?: boolean;
    placeholder?: string;
    forceSelection?: boolean;
};

const UserIcon = withIcon(faUser);
const CheckIcon = withIcon(faCheck);
const ChevronDownIcon = withIcon(faChevronDown);
const EllipsisIcon = withIcon(faEllipsis);

const CommandInput = ({ className, ...props }: React.ComponentPropsWithRef<typeof CommandPrimitive.Input>) => (
    <CommandPrimitive.Input
        className={classnames(
            'flex flex-1 rounded px-8 py-1 transition-colors bg-surface-transparent hover:bg-surface-accent-strong-hover focus:bg-surface-weak focus:text-content-strong text-sm text-content-white dark:text-content-black font-medium outline-none',
            className
        )}
        cmdk-input-wrapper=""
        {...props}
        onFocus={(event) => {
            event.target.select();
            props.onFocus?.(event);
        }}
    />
);

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = ({ className, ...props }: React.ComponentPropsWithRef<typeof CommandPrimitive.List>) => (
    <CommandPrimitive.List
        className={classnames('max-h-[300px] overflow-y-auto overflow-x-hidden', className)}
        {...props}
    />
);

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandGroup = ({ className, ...props }: React.ComponentPropsWithRef<typeof CommandPrimitive.Group>) => (
    <CommandPrimitive.Group
        className={classnames(
            'overflow-hidden p-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium',
            className
        )}
        {...props}
    />
);

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandItem = ({ className, ...props }) => (
    <CommandPrimitive.Item
        className={classnames(
            'relative text-content-strong flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-surface-strong focus:bg-surface-strong aria-selected:bg-surface-strong data-[disabled="true"]:pointer-events-none data-[disabled="true"]:opacity-50',
            className
        )}
        {...props}
    />
);

CommandItem.displayName = CommandPrimitive.Item.displayName;

const SupportUserSelect = ({
    options,
    placeholder,
    value,
    onValueChange,
    onSelect,
    disabled,
    isLoading = false,
    forceSelection = true,
}: SupportUserSelectProps) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const [isOpen, setOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState<string>(value || '');
    const [inputValue, setInputValue] = useState<string>(value || '');

    useEffect(() => {
        setInputValue(value || '');
        setSelectedValue(value || '');
    }, [value]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            const input = inputRef.current;

            if (!input) {
                return;
            }

            // Keep the options displayed when the user is typing
            if (!isOpen) {
                setOpen(true);
            }

            // This is not a default behaviour of the <input /> field
            if (event.key === 'Enter' && input.value !== '') {
                const optionToSelect = options.find((option) => option.label === input.value);
                if (optionToSelect) {
                    const selectEvent = new Event('select', { cancelable: true });
                    input.dispatchEvent(selectEvent);
                    onSelect?.(selectEvent, optionToSelect);

                    if (!selectEvent.defaultPrevented) {
                        setSelectedValue(optionToSelect.value);
                        onValueChange?.(optionToSelect.value);
                    }
                }
            }

            if (event.key === 'Escape') {
                input.blur();
                event.preventDefault();
                event.stopPropagation();
            }
        },
        [isOpen, options, onValueChange]
    );

    const handleBlur = useCallback(() => {
        setOpen(false);
        if (forceSelection) setInputValue(value || '');
    }, [value]);

    const handleSelectOption = useCallback(
        (selectedOption: Option) => {
            const input = inputRef.current;

            if (!input) {
                return;
            }

            const selectEvent = new Event('select', { cancelable: true });
            input.dispatchEvent(selectEvent);
            onSelect?.(selectEvent, selectedOption);

            if (!selectEvent.defaultPrevented) {
                setInputValue(selectedOption.label);
                setSelectedValue(selectedOption.value);

                onValueChange?.(selectedOption.value);
            }

            // This is a hack to prevent the input from being focused after the user selects an option
            // We can call this hack: "The next tick"
            setTimeout(() => {
                inputRef?.current?.blur();
            }, 0);
        },
        [onValueChange, onSelect, inputRef]
    );

    const handleValueChange = useCallback(
        (value: string) => {
            setInputValue(value);

            const optionToSelect = options.find((option) => option.label === value || option.value === value);
            if (optionToSelect) {
                setSelectedValue(optionToSelect?.value || '');
            }

            if (!forceSelection) onValueChange?.(value);
        },
        [onValueChange, options]
    );

    const labelByValue = Object.fromEntries(
        options.map((option: any) => [option.value.toLowerCase(), option.label.toLowerCase()])
    );

    const filterOption = (value: string, search: string) => {
        const label = labelByValue[value.toLowerCase()];
        const isMatch =
            value.toLowerCase().includes(search.toLowerCase()) || label?.toLowerCase().includes(search.toLowerCase());

        // TODO: replace with similarity score
        return isMatch ? 1 : 0;
    };

    return (
        <CommandPrimitive onKeyDown={handleKeyDown} filter={filterOption}>
            <div className="relative flex group">
                <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-content-white dark:text-content-black text-xs z-20 pointer-events-none group-focus-within:text-content-strong" />
                <span className="flex-1 block px-8 py-1 text-sm font-medium min-w-0 group-focus-within:min-w-[280px] transition-all max-w-[280px] overflow-hidden invisible whitespace-pre">
                    {inputValue || '.'}
                </span>
                <CommandInput
                    ref={inputRef}
                    value={inputValue}
                    onValueChange={handleValueChange}
                    onBlur={handleBlur}
                    onFocus={() => setOpen(true)}
                    placeholder={placeholder}
                    disabled={disabled}
                    className="absolute inset-0 w-full z-10"
                />
                <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 text-content-white dark:text-content-black text-xs z-20 pointer-events-none group-focus-within:text-content-strong" />
            </div>

            <div className="relative z-50">
                {isOpen ? (
                    <div className="absolute top-1 z-50 min-w-full max-h-96 rounded-md border border-line-weak bg-surface-weak shadow-md outline-none animate-in fade-in-0 zoom-in-95">
                        <CommandList className="ring-1 ring-slate-200 rounded-lg">
                            {isLoading ? (
                                <CommandPrimitive.Loading>
                                    <div className="p-6 text-center">
                                        <FontAwesomeIcon
                                            icon="circle-notch"
                                            spin
                                            className="text-2xl text-content-weak"
                                        />
                                    </div>
                                </CommandPrimitive.Loading>
                            ) : null}
                            {options.length > 0 && !isLoading ? (
                                <CommandGroup>
                                    {options.map((option) => {
                                        const isSelected = selectedValue === option.value;
                                        return (
                                            <CommandItem
                                                key={option.value}
                                                value={option.value}
                                                onMouseDown={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                }}
                                                onSelect={(e) => handleSelectOption(option)}
                                                className={classnames(
                                                    'flex items-center gap-2 pr-8 pl-4 w-full',
                                                    !isSelected ? 'pr-8 pl-4' : null
                                                )}
                                            >
                                                <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                                                    {isSelected ? <CheckIcon className="h-3 w-3" /> : null}
                                                </span>

                                                {option.label === option.value ? (
                                                    <span>{option.label}</span>
                                                ) : (
                                                    <span>
                                                        <span>{option.label}</span>
                                                        <span className="block text-xs text-content-weak">
                                                            {option.value}
                                                        </span>
                                                    </span>
                                                )}
                                            </CommandItem>
                                        );
                                    })}
                                </CommandGroup>
                            ) : null}
                            {!isLoading ? (
                                <CommandPrimitive.Empty>
                                    <div className="p-3 text-center">
                                        <EllipsisIcon className="text-sm text-content-weak" />
                                    </div>
                                </CommandPrimitive.Empty>
                            ) : null}
                        </CommandList>
                    </div>
                ) : null}
            </div>
        </CommandPrimitive>
    );
};

export default SupportUserSelect;
